import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, ArrowUpDown, Plus, X } from "lucide-react";
import SectionCard from "components/SectionCard";
import ModalBackdrop from "components/ModalBackdrop";
import { DropdownSelect, TextInput } from "components/InputStyled";
import { useDialog } from "context/DialogContext";
import { useProfile } from "context/ProfileContext";
import { logAudit } from "lib/audit";
import { ORG_ASSETS_BUCKET } from "reporting/pdf/orgLogo";
import { supabase } from "services/supabaseClient";

const MAX_LOGO_BYTES = 2 * 1024 * 1024;
const clean = (value) => String(value ?? "").trim();

function compare(a, b, ascending) {
  const result = String(a ?? "").localeCompare(String(b ?? ""), undefined, {
    numeric: true,
    sensitivity: "base",
  });
  return ascending ? result : -result;
}

function StatusPill({ status }) {
  const active = status !== "inactive";
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${active ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-700"}`}>
      {active ? "Active" : "Inactive"}
    </span>
  );
}

export default function OrgSetup() {
  const { profile, loading: profileLoading } = useProfile();
  const { alert, confirm } = useDialog();
  const isPlatformAdmin = !!profile?.is_admin;
  const [orgs, setOrgs] = useState([]);
  const [parties, setParties] = useState([]);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState("name");
  const [sortAsc, setSortAsc] = useState(true);
  const [editingOrgId, setEditingOrgId] = useState(null);
  const [creating, setCreating] = useState(false);

  const loadData = useCallback(async () => {
    if (!isPlatformAdmin) return;
    setLoading(true);
    const [orgResult, partyResult, linkResult] = await Promise.all([
      supabase.from("orgs").select("id, name, legal_name, status, pdf_logo_path").order("name"),
      supabase.from("parties").select("id, canon_name").order("canon_name"),
      supabase.from("org_party_links").select("org_id, party_id"),
    ]);
    setLoading(false);
    const error = orgResult.error || partyResult.error || linkResult.error;
    if (error) {
      await alert("Failed to load organizations: " + error.message, { title: "Organizations" });
      return;
    }
    setOrgs(orgResult.data || []);
    setParties(partyResult.data || []);
    setLinks(linkResult.data || []);
  }, [alert, isPlatformAdmin]);

  useEffect(() => {
    if (!profileLoading) void loadData();
  }, [loadData, profileLoading]);

  const partyIdsByOrg = useMemo(() => {
    const map = new Map();
    links.forEach(({ org_id: orgId, party_id: partyId }) => {
      if (!map.has(orgId)) map.set(orgId, []);
      map.get(orgId).push(partyId);
    });
    return map;
  }, [links]);

  const visibleOrgs = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return orgs
      .filter((org) => [org.name, org.legal_name, org.status].some((value) => String(value || "").toLowerCase().includes(needle)))
      .sort((a, b) => {
        return compare(a[sortKey], b[sortKey], sortAsc);
      });
  }, [orgs, query, sortAsc, sortKey]);

  const handleSort = (key) => {
    if (sortKey === key) setSortAsc((value) => !value);
    else { setSortKey(key); setSortAsc(true); }
  };
  const sortIcon = (key) => sortKey !== key
    ? <ArrowUpDown className="h-3.5 w-3.5" />
    : sortAsc ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />;

  if (profileLoading || loading) return <div className="p-5 text-muted">Loading…</div>;
  if (!isPlatformAdmin) return <div className="p-5 text-red-600">Platform admin access required.</div>;

  const editingOrg = editingOrgId ? orgs.find((org) => org.id === editingOrgId) : null;
  const closeModal = () => { setEditingOrgId(null); setCreating(false); };

  return (
    <div className="space-y-6">
      <SectionCard>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-lg font-semibold text-app">Organizations</div>
            <div className="text-sm text-muted">View and manage organization configuration.</div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input value={query} onChange={(event) => setQuery(event.target.value)} className="h-8 w-72 rounded-md border border-input bg-background px-3 text-sm text-foreground" placeholder="Search organizations..." />
            <button type="button" className="btn btn-ghost h-8 px-3 text-sm" onClick={loadData}>Refresh</button>
            <button type="button" className="btn btn-primary h-8 px-3 text-sm" onClick={() => setCreating(true)}><Plus className="mr-1.5 h-4 w-4" />New</button>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-app">
          <table className="w-full table-fixed text-sm">
            <thead className="bg-app text-muted shadow-[inset_0_-1px_0_hsl(var(--border))]">
              <tr className="select-none">
                {[["name", "Organization", "w-[50%]"], ["status", "Status", "w-[18%]"], ["pdf_logo_path", "PDF Logo", "w-[22%]"]].map(([key, label, width]) => (
                  <th key={key} className={`cursor-pointer px-4 py-2 text-left font-bold ${width}`} onClick={() => handleSort(key)}>
                    <div className="flex items-center gap-1"><span className="truncate">{label}</span>{sortIcon(key)}</div>
                  </th>
                ))}
                <th className="w-24 px-4 py-2 text-right font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!visibleOrgs.length ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center italic text-muted">No organizations found.</td></tr>
              ) : visibleOrgs.map((org) => (
                <tr key={org.id} className="border-t border-app hover:bg-app/50">
                  <td className="truncate px-4 py-3 font-medium text-app" title={org.name}>{org.name}</td>
                  <td className="px-4 py-3"><StatusPill status={org.status} /></td>
                  <td className="px-4 py-3 text-app">{org.pdf_logo_path ? "Configured" : "Not configured"}</td>
                  <td className="px-4 py-3 text-right"><button type="button" className="btn btn-ghost h-8 px-2 text-sm" onClick={() => setEditingOrgId(org.id)}>View/Edit</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {(creating || editingOrg) && (
        <OrgConfigurationModal
          org={editingOrg}
          parties={parties}
          initialPartyIds={editingOrg ? partyIdsByOrg.get(editingOrg.id) || [] : []}
          tenantId={profile.tenant_id}
          alert={alert}
          confirm={confirm}
          onClose={closeModal}
          onSaved={async () => { closeModal(); await loadData(); }}
        />
      )}
    </div>
  );
}

function OrgConfigurationModal({ org, parties, initialPartyIds, tenantId, alert, confirm, onClose, onSaved }) {
  const isNew = !org;
  const [draft, setDraft] = useState({ name: org?.name || "", legalName: org?.legal_name || "", status: org?.status || "active", partyIds: initialPartyIds, logoFile: null, removeLogo: false });
  const [partyToAdd, setPartyToAdd] = useState("");
  const [existingLogoUrl, setExistingLogoUrl] = useState("");
  const [newLogoUrl, setNewLogoUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [createdOrgId, setCreatedOrgId] = useState(null);

  useEffect(() => {
    let url = "";
    let cancelled = false;
    if (!org?.pdf_logo_path) return undefined;
    supabase.storage.from(ORG_ASSETS_BUCKET).download(org.pdf_logo_path).then(({ data }) => {
      if (cancelled || !data) return;
      url = URL.createObjectURL(data);
      setExistingLogoUrl(url);
    });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [org?.pdf_logo_path]);

  useEffect(() => {
    if (!draft.logoFile) { setNewLogoUrl(""); return undefined; }
    const url = URL.createObjectURL(draft.logoFile);
    setNewLogoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [draft.logoFile]);

  const originalParties = [...initialPartyIds].sort().join(",");
  const dirty = clean(draft.name) !== clean(org?.name) || clean(draft.legalName) !== clean(org?.legal_name) || draft.status !== (org?.status || "active") || [...draft.partyIds].sort().join(",") !== originalParties || !!draft.logoFile || draft.removeLogo;

  const close = async () => {
    if (dirty) {
      const discard = await confirm("Discard unsaved organization changes?", { title: "Unsaved changes", okLabel: "Discard", cancelLabel: "Keep editing" });
      if (!discard) return;
    }
    onClose();
  };

  const chooseLogo = async (file) => {
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type)) { await alert("Choose a PNG or JPEG image.", { title: "Organization logo" }); return; }
    if (file.size > MAX_LOGO_BYTES) { await alert("The logo must be 2 MB or smaller.", { title: "Organization logo" }); return; }
    setDraft((value) => ({ ...value, logoFile: file, removeLogo: false }));
  };

  const save = async () => {
    const name = clean(draft.name);
    if (!name) { await alert("Organization name is required.", { title: "Organization" }); return; }
    setSaving(true);
    try {
      let orgId = org?.id || createdOrgId;
      if (!orgId) {
        const { data, error } = await supabase.from("orgs").insert({ name, legal_name: clean(draft.legalName) || null, status: draft.status, tenant_id: tenantId }).select("id").single();
        if (error) throw error;
        orgId = data.id;
        setCreatedOrgId(orgId);
      } else {
        const { error } = await supabase.from("orgs").update({ name, legal_name: clean(draft.legalName) || null, status: draft.status }).eq("id", orgId);
        if (error) throw error;
      }

      let logoPath = org?.pdf_logo_path || null;
      if (draft.logoFile) {
        const extension = draft.logoFile.type === "image/jpeg" ? "jpg" : "png";
        const nextPath = `${orgId}/pdf-logo.${extension}`;
        const { error } = await supabase.storage.from(ORG_ASSETS_BUCKET).upload(nextPath, draft.logoFile, { contentType: draft.logoFile.type, upsert: true });
        if (error) throw error;
        if (logoPath && logoPath !== nextPath) await supabase.storage.from(ORG_ASSETS_BUCKET).remove([logoPath]);
        logoPath = nextPath;
      } else if (draft.removeLogo && logoPath) {
        await supabase.storage.from(ORG_ASSETS_BUCKET).remove([logoPath]);
        logoPath = null;
      }
      if (logoPath !== (org?.pdf_logo_path || null)) {
        const { error } = await supabase.from("orgs").update({ pdf_logo_path: logoPath }).eq("id", orgId);
        if (error) throw error;
      }

      const original = new Set(initialPartyIds);
      const next = new Set(draft.partyIds);
      const removed = [...original].filter((id) => !next.has(id));
      const added = [...next].filter((id) => !original.has(id));
      if (removed.length) {
        const { error } = await supabase.from("org_party_links").delete().eq("org_id", orgId).in("party_id", removed);
        if (error) throw error;
      }
      if (added.length) {
        const { error } = await supabase.from("org_party_links").insert(added.map((partyId) => ({ org_id: orgId, party_id: partyId, primary_flag: false })));
        if (error) throw error;
      }

      await logAudit({ category: "org", action: isNew ? "org_created" : "org_updated", success: true, detail: { org_id: orgId, name, linked_party_count: draft.partyIds.length, pdf_logo_configured: !!logoPath } });
      await onSaved();
    } catch (error) {
      await alert("Unable to save organization: " + error.message, { title: "Organization" });
    } finally { setSaving(false); }
  };

  const selectedParties = draft.partyIds.map((id) => parties.find((party) => party.id === id)).filter(Boolean).sort((a, b) => a.canon_name.localeCompare(b.canon_name));
  const availableParties = parties.filter((party) => !draft.partyIds.includes(party.id));
  const previewUrl = newLogoUrl || (!draft.removeLogo ? existingLogoUrl : "");

  return (
    <ModalBackdrop>
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg border border-app bg-app shadow-xl" role="dialog" aria-modal="true" aria-label={isNew ? "New organization" : `Edit ${org.name}`}>
        <div className="flex items-start justify-between gap-4 border-b border-app p-5">
          <div><div className="text-lg font-semibold text-app">{isNew ? "New Organization" : org.name}</div><div className="text-sm text-muted">Organization configuration and report branding.</div></div>
          <button type="button" className="btn btn-ghost h-8 px-2" onClick={close} aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
        <div className="min-h-0 flex-1 space-y-6 overflow-auto p-5">
          <div>
            <div className="mb-3 text-sm font-semibold text-app">Details</div>
            <div className="grid gap-3 md:grid-cols-2">
              <TextInput label="Organization Name" value={draft.name} onChange={(name) => setDraft((value) => ({ ...value, name }))} className="w-full" />
              <TextInput label="Legal Name (optional)" value={draft.legalName} onChange={(legalName) => setDraft((value) => ({ ...value, legalName }))} className="w-full" />
              <DropdownSelect label="Status" value={draft.status} onChange={(status) => setDraft((value) => ({ ...value, status }))} className="w-full" options={[{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }]} />
            </div>
          </div>
          <div className="border-t border-app pt-5">
            <div className="text-sm font-semibold text-app">PDF Logo</div>
            <div className="mt-1 text-xs text-muted">PNG or JPEG, up to 2 MB. Used by DebtStudio and FundPlanner PDFs.</div>
            <div className="mt-3 flex flex-wrap items-end gap-3">
              <div className="flex h-20 w-48 items-center justify-center rounded border border-app bg-white p-2">{previewUrl ? <img src={previewUrl} alt="PDF logo preview" className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-gray-500">No logo configured</span>}</div>
              <label className="flex flex-col gap-1 text-xs font-medium text-muted">Logo file<input type="file" accept="image/png,image/jpeg,.png,.jpg,.jpeg" className="input-app h-10 p-1 text-sm" onChange={(event) => void chooseLogo(event.target.files?.[0])} disabled={saving} /></label>
              {previewUrl && <button type="button" className="btn btn-ghost border border-app text-red-600" onClick={() => setDraft((value) => ({ ...value, logoFile: null, removeLogo: true }))}>Remove</button>}
            </div>
          </div>
          <div className="border-t border-app pt-5">
            <div className="text-sm font-semibold text-app">Linked Trade Parties</div>
            <div className="mt-3 flex items-end gap-2">
              <DropdownSelect label="Trade Party" value={partyToAdd} onChange={setPartyToAdd} placeholder="Select a party" className="w-full" options={availableParties.map((party) => ({ value: party.id, label: party.canon_name }))} />
              <button type="button" className="btn btn-ghost h-8 border border-app px-3 text-sm" disabled={!partyToAdd} onClick={() => { setDraft((value) => ({ ...value, partyIds: [...value.partyIds, partyToAdd] })); setPartyToAdd(""); }}>Add</button>
            </div>
            <div className="mt-3 overflow-hidden rounded-lg border border-app">
              {!selectedParties.length ? <div className="p-4 text-center text-sm italic text-muted">No linked parties.</div> : selectedParties.map((party) => (
                <div key={party.id} className="flex items-center justify-between border-t border-app px-3 py-2 first:border-t-0"><span className="text-sm text-app">{party.canon_name}</span><button type="button" className="btn btn-ghost h-7 px-2 text-xs text-red-600" onClick={() => setDraft((value) => ({ ...value, partyIds: value.partyIds.filter((id) => id !== party.id) }))}>Remove</button></div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-app p-4">
          <button type="button" className="btn btn-ghost border border-app" onClick={close} disabled={saving}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving || !clean(draft.name)}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </ModalBackdrop>
  );
}
